import { supabase } from "@/lib/supabase";
import { JobService } from "./job.service";
import { SubcontractorAssignmentService } from "./subcontractor-assignment.service";
import { IssueService } from "./issue.service";
import { QAService } from "./qa.service";
import type { Job, CloseoutStatus, Issue } from "@/data/types";
import type { QASubmission } from "./qa.service";

// ── Shape returned by getQueue ─────────────────────────────────────────────
export interface CloseoutQueueItem {
  job: Job;
  qaSubmission: QASubmission | null;
}

// ── Shape of the admin reviewer checklist (persisted in job metadata) ──────
export interface ReviewerChecklist {
  materials: boolean;
  defects: boolean;
  finish: boolean;
  scope: boolean;
  photos: boolean;
}

function toSortableTimestamp(value: string | undefined): number {
  if (!value) return 0;
  const ts = Date.parse(value);
  return Number.isFinite(ts) ? ts : 0;
}

function selectLatestSubmission(
  current: QASubmission | undefined,
  candidate: QASubmission,
): QASubmission {
  if (!current) return candidate;
  const currentTs = toSortableTimestamp(current.submittedAt);
  const candidateTs = toSortableTimestamp(candidate.submittedAt);
  if (candidateTs > currentTs) return candidate;
  if (candidateTs < currentTs) return current;
  if (current.status === "Draft" && candidate.status === "Submitted") return candidate;
  return current;
}

// ── Service ───────────────────────────────────────────────────────────────
export const CloseoutService = {
  /**
   * Fetch all jobs that are in the QA/Closeout pipeline
   * (stage order >= 90, i.e. QA Review or Completed)
   * and attach their QA submission record.
   */
  async getQueue(): Promise<{ data: CloseoutQueueItem[] | null; error: unknown }> {
    // Fetch all jobs and all QA submissions in parallel
    const [jobsResult, qaResult] = await Promise.all([
      JobService.getAll(),
      QAService.getAll(),
    ]);

    if (jobsResult.error || !jobsResult.data) return { data: null, error: jobsResult.error };

    const jobs = jobsResult.data;
    const allSubmissions: QASubmission[] = qaResult.data ?? [];

    // Build a lookup: jobId → latest QA submission (robust even if source ordering changes)
    const submissionByJob = new Map<string, QASubmission>();
    for (const sub of allSubmissions) {
      submissionByJob.set(sub.jobId, selectLatestSubmission(submissionByJob.get(sub.jobId), sub));
    }

    // A job belongs in the pipeline if:
    //   - it has a meaningful closeout status, OR
    //   - it's at QA Review / Completed stage, OR
    //   - it has a QA submission (even if stage update failed)
    const pipelineJobs = jobs.filter(
      (j) =>
        j.closeoutStatus !== "Not Started" ||
        j.currentStage === "QA Review" ||
        j.currentStage === "Completed" ||
        j.qaSubmitted === true ||
        submissionByJob.has(j.id)
    );

    const queue: CloseoutQueueItem[] = pipelineJobs.map((job) => ({
      job,
      qaSubmission: submissionByJob.get(job.id) ?? null,
    }));

    return { data: queue, error: null };
  },

  /**
   * Fetch a single job's closeout record with its QA submission.
   */
  async getByJobNumber(jobNumber: string): Promise<{ data: CloseoutQueueItem | null; error: unknown }> {
    const { data: job, error } = await JobService.getById(jobNumber);
    if (error || !job) return { data: null, error };

    const { data: submissions } = await QAService.getByJobNumber(jobNumber);
    return {
      data: { job, qaSubmission: submissions?.[0] ?? null },
      error: null,
    };
  },

  /**
   * Approve a job — moves it to Completed, sets QA status to Passed,
   * and advances the linked installer_payouts row from pending_review → approved.
   * Persists the reviewer checklist results into job metadata.
   *
   * @param jobId - jobs.id UUID (used to filter installer_payouts.execution_id)
   */
  async approve(
    jobId: string,
    reviewerChecklist: ReviewerChecklist,
    notes?: string,
    customerSignoff?: boolean,
  ): Promise<{ data: Job | null; error: unknown }> {
    const result = await JobService.update(jobId, {
      closeoutStatus: "Closed" as CloseoutStatus,
      currentStage: "Completed" as any,
      qaStatus: "Passed",
      qaSubmitted: true,
      closeoutNotes: notes || "QA approved and closed",
      finalDocumentationComplete: true,
      customerSignoff: Boolean(customerSignoff),
      reviewSatisfied: null,
      qaReviewerChecklist: reviewerChecklist,
    });

    if (result.error || !result.data) return result;

    // Advance installer payout pending_review → approved through an Edge
    // Function so the service-role key never ships to the browser.
    const { error: payoutErr } = await supabase.functions.invoke("closeout-approve-payout", {
      body: { jobId },
    });

    if (payoutErr) {
      console.error("[CloseoutService.approve] installer_payouts sync failed:", payoutErr.message);
      // Non-fatal — job is already closed; payout can be reconciled manually.
    }

    // Advance any active sub-contractor assignments to complete.
    void SubcontractorAssignmentService.advanceToComplete(jobId);

    return result;
  },

/**
    * Send a job back to Operations for issue resolution.
    */
  async sendBack(
    jobNumber: string,
    notes?: string
  ): Promise<{ data: Job | null; error: unknown }> {
    return JobService.update(jobNumber, {
      closeoutStatus: "Install Complete" as CloseoutStatus,
      closeoutNotes: notes || "Sent back to Ops for resolution",
      qaStatus: "Failed",
    });
  },

  /**
    * Fail QA — marks job as failed, creates punch list issues, keeps in QA Review stage.
    */
  async fail(
    jobNumber: string,
    notes?: string,
    punchListItems?: Array<{ description: string; severity: string }>
  ): Promise<{ data: Job | null; error: unknown }> {
    if (punchListItems && punchListItems.length > 0) {
      const normalizeSeverity = (severity: string): "Low" | "Medium" | "Urgent" => {
        const value = String(severity || "").trim().toLowerCase();
        if (value === "urgent" || value === "high") return "Urgent";
        if (value === "medium") return "Medium";
        return "Low";
      };
      for (const item of punchListItems) {
        const issue: Issue = {
          id: `PUNCH-${Date.now().toString(36).toUpperCase()}`,
          jobId: jobNumber,
          description: `[QA Failed] ${item.description}`,
          issueType: "Punch List",
          severity: normalizeSeverity(item.severity),
          status: "Open",
          owner: "QA Inspector",
          photo: "",
          dateCreated: new Date().toISOString().slice(0, 10),
          source: "QA Inspector",
        };
        await IssueService.create(issue);
      }
    }

    return JobService.update(jobNumber, {
      qaStatus: "Failed",
      closeoutNotes: notes || "QA failed - punch list created",
      issueFlag: true,
    });
  },

  /**
   * Record the customer satisfaction response after job closure.
   * - satisfied = true  → mark review sent
   * - satisfied = false → create internal complaint issue, block review
   */
  async recordSatisfaction(
    jobNumber: string,
    satisfied: boolean
  ): Promise<{ data: Job | null; error: unknown }> {
    if (satisfied) {
      return JobService.update(jobNumber, {
        reviewSatisfied: true,
        reviewSent: true,
        reviewSentAt: new Date().toISOString(),
      });
    } else {
      // Create internal complaint issue
      const issue: Issue = {
        id: `CS-${Date.now().toString(36).toUpperCase()}`,
        jobId: jobNumber,
        description: "[Post-Close Concern] Customer indicated dissatisfaction after QA approval",
        issueType: "Customer Complaint",
        severity: "Urgent",
        status: "Open",
        owner: "Ops Manager",
        photo: "",
        dateCreated: new Date().toISOString().slice(0, 10),
        source: "QA Inspector",
      };
      await IssueService.create(issue);

      return JobService.update(jobNumber, { reviewSatisfied: false });
    }
  },

  /**
   * Mark that the customer submitted their review.
   */
  async markReviewCompleted(jobNumber: string): Promise<{ data: Job | null; error: unknown }> {
    return JobService.update(jobNumber, { reviewCompleted: true });
  },

  /**
   * Send a referral request to the customer.
   */
  async sendReferralRequest(jobNumber: string): Promise<{ data: Job | null; error: unknown }> {
    return JobService.update(jobNumber, {
      referralRequested: true,
      referralRequestedAt: new Date().toISOString(),
    });
  },

  /**
   * Mark a referral as received.
   */
async markReferralReceived(jobNumber: string): Promise<{ data: Job | null; error: unknown }> {
     return JobService.update(jobNumber, { referralReceived: true });
   },

   /**
    * Send a contractor rating request to the customer.
    * Generates a contractor-specific rating link for the assigned installer.
    */
 async sendContractorRatingRequest(jobNumber: string): Promise<{ data: Job | null; error: unknown }> {
    // Use a placeholder URL - the actual domain should be configured in production
    const ratingUrl = `/portal/${jobNumber}/rate-contractor`;
    const jobResult = await JobService.getById(jobNumber);
    const job = jobResult.data;

    let emailError: unknown = null;

    if (job && job.email) {
      const contractorName = job.assignedCrew || job.assignedSubcontractor || "Your Contractor";
      try {
        const { error } = await supabase.functions.invoke("send-rating-email", {
          body: {
            toEmail: job.email,
            customerName: job.customerName,
            contractorName,
            jobNumber,
          },
        });
        if (error) {
          emailError = error;
        }
      } catch (e) {
        emailError = e;
      }
    }

    // Always mark the DB record
    const result = await JobService.update(jobNumber, {
      contractorRatingSent: true,
      contractorRatingUrl: ratingUrl,
      contractorRatingAt: new Date().toISOString(),
    });

    if (emailError) {
      console.error("Failed to send rating email:", emailError);
      return {
        ...result,
        error: new Error(
          `Database updated but email failed to send: ${emailError instanceof Error ? emailError.message : String(emailError)}`
        ),
      };
    }

    return result;
  },

  /**
   * Mark that the customer completed the contractor rating.
   */
   async markContractorRatingCompleted(jobNumber: string): Promise<{ data: Job | null; error: unknown }> {
     return JobService.update(jobNumber, { contractorRatingCompleted: true });
   },

   /**
    * Create a service request (punch list / warranty / concern) linked to the job.
    */
  async createServiceRequest(
    jobNumber: string,
    type: string,
    description: string,
    owner = "QA Inspector"
  ): Promise<{ data: Issue | null; error: unknown }> {
    const issue: Issue = {
      id: `SR-${Date.now().toString(36).toUpperCase()}`,
      jobId: jobNumber,
      description: `[QA ${type}] ${description}`,
      issueType: type === "Punch List" ? "Install Issue" : "Customer Complaint",
      severity: "Medium",
      status: "Open",
      owner,
      photo: "",
      dateCreated: new Date().toISOString().slice(0, 10),
      source: "QA Inspector",
    };
    const { data, error } = await IssueService.create(issue);
    return { data, error };
  },
};
